// Which chat channel the user is looking at right now, if any.
//
// Read by the foreground notification handler (lib/notification-foreground.ts) to decide whether
// an arriving push is about the conversation already on screen. That reader is the reason this is
// a module-level register and not a context: `setNotificationHandler` takes a plain callback that
// the OS bridge invokes outside React entirely — there is no component around it, no hook it could
// call, and no render it happens during. A context would have to be mirrored out into a module
// variable anyway, so this is that variable without the ceremony.
//
// The failure this file is shaped to avoid is a STALE entry. An extra banner is a small annoyance;
// a channel id left behind by a screen the user has left silently swallows every notification for
// that conversation, with nothing on screen to explain it. Hence: the writer clears on blur, not
// merely on unmount (see useFocusEffect in the channel screen), and the clear is claim-checked
// below so it can never revoke somebody else's.

let activeChannelId: string | null = null;

/** Claim the screen. Called when a channel screen gains focus. */
export function setActiveChannel(channelId: string): void {
    activeChannelId = channelId;
}

/**
 * Release the claim — but only if it is still ours.
 *
 * Navigating from one channel straight to another interleaves a blur and a focus, and React does
 * not promise which side runs first. Without the check, a late cleanup from the channel being left
 * would wipe the claim the channel being opened has already made, and the new screen would show
 * banners for the conversation the user is reading.
 */
export function clearActiveChannel(channelId: string): void {
    if (activeChannelId === channelId) {
        activeChannelId = null;
    }
}

/** True when this channel is the one on screen. False for every channel while none is. */
export function isActiveChannel(channelId: string): boolean {
    return activeChannelId !== null && activeChannelId === channelId;
}
